import { Component, OnInit, OnDestroy, ViewChild, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostBinding } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { FileReviewData, SeparatedDocument, SeparatedDocPage } from '../../models/file-review.model';
import { FileReviewService } from '../../services/file-review.service';
import { StatusService } from '../../services/status.service';
import { PageGalleryComponent } from '../../components/page-gallery/page-gallery.component';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { Subscription } from 'rxjs';
import { MultipartDocumentStatus } from '../../models/mutipart-document-status.enum';

@Component({
  selector: 'app-file-review',
  templateUrl: './file-review.component.html',
  styleUrls: ['./file-review.component.scss']
})
export class FileReviewComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild(PageGalleryComponent) pageGallery?: PageGalleryComponent;

  // Embedded/modal mode: when true, file_id comes from input rather than route,
  // and the component emits close/completed events instead of navigating.
  @Input() embedded = false;
  @Input() inputFileId: string | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() reviewCompleted = new EventEmitter<void>();

  @HostBinding('class.embedded') get hostEmbeddedClass(): boolean {
    return this.embedded;
  }

  private SUBSCRIPTIONS: Subscription[] = [];
  // Active file id (from route in standalone mode, from input in embedded mode)
  file_id: string | null = null;

  // Loading states
  isLoading = false;

  // Parent state - what the parent needs to know
  file_review_data: FileReviewData | null = null;
  selected_doc_id: string | null = null;
  
  // Modal state
  showCreateFileModal = false;

  // Approval state - track which documents are approved locally
  approvedDocuments: Set<string> = new Set();
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fileReviewService: FileReviewService,
    private statusService: StatusService,
    private toasterService: ToasterService
  ) { }

  ngOnInit(): void {
    if (this.embedded) {
      this.file_id = this.inputFileId;
      if (this.file_id) {
        this.loadFileReview();
      }
      return;
    }

    // Standalone (route) mode: read file_id from URL.
    this.SUBSCRIPTIONS.push(this.route.paramMap.subscribe(params => {
      this.file_id = params.get('file_id');
      if (this.file_id) {
        this.loadFileReview();
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.embedded && changes['inputFileId'] && !changes['inputFileId'].firstChange) {
      this.file_id = this.inputFileId;
      this.resetState();
      if (this.file_id) {
        this.loadFileReview();
      }
    }
  }

  ngOnDestroy(): void {
    this.SUBSCRIPTIONS.forEach(s => s.unsubscribe());
  }

  private resetState(): void {
    this.file_review_data = null;
    this.selected_doc_id = null;
    this.approvedDocuments = new Set();
    this.showCreateFileModal = false;
  }

  /**
   * Back button handler — navigates in standalone mode, emits close in embedded mode.
   */
  onBack(): void {
    if (this.embedded) {
      this.closed.emit();
      return;
    }
    this.router.navigate(['/files/review']);
  }

  /**
   * Loads the file review data from the API
   */
  private async loadFileReview(): Promise<void> {
    try {
      this.isLoading = true;
      this.file_review_data = await this.fileReviewService.getFileReviewData(this.file_id!);
      
      // Auto-select the first document if available
      if (this.file_review_data.separated_documents.length > 0) {
        this.selected_doc_id = this.file_review_data.separated_documents[0].doc_id;
      }
      if (this.file_review_data.status == MultipartDocumentStatus.SEPARATION_APPROVED 
        || this.file_review_data.status == MultipartDocumentStatus.SEPARATION_COMPLETED) {
          this.file_review_data.separated_documents.forEach(doc => {
              doc.is_approved = true;
              this.approvedDocuments.add(doc.doc_id);
          });
          // Update approved count to reflect all documents are approved
          this.file_review_data.approved_count = this.file_review_data.total_count;
      }
    } catch (error) {
      alert('Error loading file: ' + error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handles document selection from the sidebar
   * @param doc_id - The ID of the selected document
   */
  onDocumentSelected(doc_id: string): void {
    this.selected_doc_id = doc_id;
  }

  /**
   * Handles document approval
   * @param doc_id - The ID of the document to approve
   */
  async onApproveDocument(doc_id: string): Promise<void> {
    try {
      // Add to approved set locally
      this.approvedDocuments.add(doc_id);
      
      // Update local state UI
      if (this.file_review_data) {
        const doc = this.file_review_data.separated_documents.find(d => d.doc_id === doc_id);
        if (doc) {
          doc.is_approved = true;
          this.file_review_data.approved_count++;
        }
      }

      this.toasterService.success('Document approved', 2000);

      // Check if all documents are approved
      if (this.areAllDocumentsApproved()) {
        // All approved - make API call to approve the parent document
        await this.approveAllDocumentsAPI();
        this.toasterService.success('All documents approved and locked successfully', 3000);
      }
    } catch (error) {
      this.toasterService.warning('Error approving document', 3000);
    }
  }

  /**
   * Check if all documents are approved
   * Ignores empty documents (0 pages) - only checks documents with pages
   */
  private areAllDocumentsApproved(): boolean {
    if (!this.file_review_data) {
      return false;
    }
    // Filter documents that have pages (page_count > 0)
    const documentsWithPages = this.file_review_data.separated_documents.filter(
      doc => doc.page_count > 0
    );
    
    // If there are no documents with pages, return false
    if (documentsWithPages.length === 0) {
      return false;
    }
    
    // Check if all documents with pages are approved
    return documentsWithPages.every(doc => doc.is_approved);
  }

  /**
   * Builds the final structure from local state for submission
   * @returns Array of child documents with their page IDs in final order
   */
  private buildFinalStructure(): Array<{ childDocumentId: string; pageIds: string[] }> {
    if (!this.file_review_data) {
      return [];
    }

    return this.file_review_data.separated_documents.map(doc => ({
      childDocumentId: doc.doc_id,
      pageIds: doc.pages.map(page => page.page_id) // Page IDs in their current order
    }));
  }

  /**
   * API call to approve and lock all documents and submit final structure
   */
  private async approveAllDocumentsAPI(): Promise<void> {
    if (!this.file_id || !this.file_review_data) {
      throw new Error('File ID or data is required');
    }

    try {
      // Build the final structure from local state
      const finalStructure = this.buildFinalStructure();

      // Submit the final structure to the API
      await this.fileReviewService.submitSeparationReview(this.file_id, finalStructure);

      if (this.embedded) {
        // Embedded mode: tell the parent so it can refresh + close the popup.
        this.reviewCompleted.emit();
        return;
      }

      // Standalone mode: navigate back to the pending separation list.
      await this.router.navigate(['/files/review']);
    } catch (error) {
      throw error;
    }
  }

  /**
   * API call to approve and lock a document
   * @param doc_id - The ID of the document to approve
   * @returns Promise that resolves when the document is approved
   */
  private async approveDocumentAPI(doc_id: string): Promise<void> {
    try {
      const result = await this.statusService.advanceDocumentStatus(doc_id);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handles page reordering within a document (local only, no API call)
   * @param event - Object containing doc_id, previousIndex, and currentIndex
   */
  async onReorderPages(event: { doc_id: string, previousIndex: number, currentIndex: number }): Promise<void> {
    if (!this.file_review_data) return;

    const doc = this.file_review_data.separated_documents.find(d => d.doc_id === event.doc_id);
    if (!doc) return;

    // Update local state only (changes are staged until finalization)
    moveItemInArray(doc.pages, event.previousIndex, event.currentIndex);
    
    // Update page_number for each page to reflect new order
    doc.pages.forEach((page, index) => {
      page.page_number = index + 1;
    });

    // Reset loading state
    if (this.pageGallery) {
      this.pageGallery.isReordering = false;
    }
    
    // Show success toast
    this.toasterService.success('Pages reordered. Changes will be saved when finalized.');
  }


  /**
   * Handles moving a page to a different document (local only, no API call)
   * @param event - Object containing page_id and target_doc_id
   */
  async onMovePageToDocument(event: { page_id: string, target_doc_id: string }): Promise<void> {
    if (!this.file_review_data) {
      return;
    }

    // Find source document (where the page currently is)
    let sourceDoc: SeparatedDocument | undefined;
    let pageToMove: SeparatedDocPage | undefined;
    
    for (const doc of this.file_review_data.separated_documents) {
      const page = doc.pages.find(p => p.page_id === event.page_id);
      if (page) {
        sourceDoc = doc;
        pageToMove = page;
        break;
      }
    }

    if (!sourceDoc || !pageToMove) {
      this.toasterService.warning('Page not found.');
      if (this.pageGallery) {
        this.pageGallery.movingPageIds.delete(event.page_id);
      }
      return;
    }

    // Find target document
    const targetDoc = this.file_review_data.separated_documents.find(d => d.doc_id === event.target_doc_id);
    if (!targetDoc) {
      this.toasterService.warning('Target document not found.');
      if (this.pageGallery) {
        this.pageGallery.movingPageIds.delete(event.page_id);
      }
      return;
    }

    if (sourceDoc.doc_id === targetDoc.doc_id) {
      // Same document, no need to move
      if (this.pageGallery) {
        this.pageGallery.movingPageIds.delete(event.page_id);
      }
      return;
    }

    // Update local state only (changes are staged until finalization)
    // Remove page from source document
    sourceDoc.pages = sourceDoc.pages.filter(p => p.page_id !== event.page_id);
    sourceDoc.page_count = sourceDoc.pages.length;
    
    // Add page to target document (at the end)
    targetDoc.pages.push(pageToMove);
    targetDoc.page_count = targetDoc.pages.length;
    
    // Update page numbers for both documents
    sourceDoc.pages.forEach((page, index) => {
      page.page_number = index + 1;
    });
    targetDoc.pages.forEach((page, index) => {
      page.page_number = index + 1;
    });

    // Reset loading state
    if (this.pageGallery) {
      this.pageGallery.movingPageIds.delete(event.page_id);
    }
    
    // Show success toast
    this.toasterService.success('Page moved. Changes will be saved when finalized.');
  }

  /**
   * Handles document rename
   * @param event - The rename event with doc_id and new_name
   */
  async onRenameDocument(event: { doc_id: string, new_name: string }): Promise<void> {
    if (!this.file_id || !this.file_review_data) {
      return;
    }

    try {
      const doc = this.file_review_data.separated_documents.find(d => d.doc_id === event.doc_id);
      if (!doc) {
        throw new Error('Document not found');
      }

      const oldName = doc.doc_name;
      
      // Optimistic update - update UI first
      doc.doc_name = event.new_name;
      doc.doc_id = event.new_name;
      
      // If this was the selected document, update selected_doc_id
      if (this.selected_doc_id === oldName) {
        this.selected_doc_id = event.new_name;
      }

      try {
        // Make API call
        await this.fileReviewService.renameDocument(this.file_id, oldName, event.new_name);
        this.toasterService.success('Category name updated successfully', 3000);
        
        // Reload data to get updated presigned URLs with new document name
        await this.loadFileReview();
      } catch (error) {
        // Revert on error
        doc.doc_name = oldName;
        doc.doc_id = oldName;
        if (this.selected_doc_id === event.new_name) {
          this.selected_doc_id = oldName;
        }
        this.toasterService.warning('Failed to update category name', 4000);
      }
    } catch (error) {
      // Error in rename handler
    }
  }

  /**
   * Opens the create file modal
   */
  onCreateNewFile(): void {
    this.showCreateFileModal = true;
  }

  /**
   * Closes the create file modal
   */
  onCloseCreateFileModal(): void {
    this.showCreateFileModal = false;
  }

  /**
   * Handles unapproving a single document
   * Removes it from the approved set and updates local state
   * @param doc_id - The ID of the document to unapprove
   */
  onUnapproveDocument(doc_id: string): void {
    try {
      // Remove from approved set locally
      this.approvedDocuments.delete(doc_id);
      
      // Update local state UI
      if (this.file_review_data) {
        const doc = this.file_review_data.separated_documents.find(d => d.doc_id === doc_id);
        if (doc) {
          doc.is_approved = false;
          this.file_review_data.approved_count--;
        }
      }

      this.toasterService.success('Document unapproved', 2000);
    } catch (error) {
      this.toasterService.warning('Error unapproving document', 3000);
    }
  }

  /**
   * Handles reverting document status
   * Only works if document has been approved/separated
   */
  async onRevertDocumentStatus(): Promise<void> {
    if (!this.file_id || !this.file_review_data) {
      return;
    }

    // Check if document is in SEPARATION_APPROVED status
    if (
      this.file_review_data.status !== MultipartDocumentStatus.SEPARATION_APPROVED 
      && this.file_review_data.status !== MultipartDocumentStatus.SEPARATION_COMPLETED
    ) {
      this.toasterService.warning(
        'You must approve all documents before resetting this process',
        4000
      );
      return;
    }

    // Show info message that process is starting
    this.toasterService.info('Resetting process...', 3000);

    try {
      const result = await this.statusService.revertDocumentStatus(this.file_id);
      this.toasterService.success('Process reset successfully', 3000);
      
      // Reload data to reflect the new status
      await this.loadFileReview();
    } catch (error: unknown) {
      // Show specific error message
      const errorMessage = (error as { error?: { message?: string } })?.error?.message || 'Failed to revert process';
      this.toasterService.warning(errorMessage, 4000);
    }
  }

  /**
   * Handles creating a new file from modal
   */
  async onCreateFileSubmit(fileName: string): Promise<void> {
    if (!this.file_id || !this.file_review_data) {
      return;
    }

    const trimmedName = fileName.trim();

    // Check if file already exists
    const exists = this.file_review_data.separated_documents.some(
      doc => doc.doc_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (exists) {
      this.toasterService.warning('A file with this name already exists!', 3000);
      return;
    }

    try {
      // Close modal immediately
      this.showCreateFileModal = false;

      // Make API call
      await this.fileReviewService.createDocument(this.file_id, trimmedName);
      this.toasterService.success('Category created successfully', 3000);
      
      // Reload data to get the new file
      await this.loadFileReview();
      
      // Select the newly created file
      this.selected_doc_id = trimmedName;
    } catch (error) {
      this.toasterService.warning('Failed to create category', 4000);
    }
  }

  /**
   * Gets the currently selected document
   * @returns The selected document or null
   */
  get selected_document(): SeparatedDocument | null {
    if (!this.file_review_data || !this.selected_doc_id) {
      return null;
    }
    return this.file_review_data.separated_documents.find(
      doc => doc.doc_id === this.selected_doc_id
    ) || null;
  }

}
